import type { Store } from "@reduxjs/toolkit";

import { AssessmentStatus } from "api/api-data-types";
import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import type { AssessmentType, PageType, SubmissionType } from "components/server-types";
import type { RootState } from "store";
import { submissionSelectors } from "store/selectors/base";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { computedStateActions } from "store/slices/computed";
import { BROADCAST_TOPICS, addBroadcastListener } from "utils/broadcast";

/** Coalesce bursts (e.g. mass collect) per page; unrelated pages stay independent. */
const DEBOUNCE_MS = 400;

const timeoutIds = new Map<string, ReturnType<typeof setTimeout>>();

async function refetchPageAssessmentStatus(store: Store<RootState>, pageId: string): Promise<void> {
  const statuses = await serverGetPageAssessmentStatuses({ pageIds: [pageId] });
  store.dispatch(computedStateActions.savePageAssessmentStatuses(statuses));
}

function scheduleRefresh(store: Store<RootState>, pageId: string) {
  const existing = timeoutIds.get(pageId);
  if (existing !== undefined) {
    clearTimeout(existing);
  }
  timeoutIds.set(
    pageId,
    setTimeout(() => {
      timeoutIds.delete(pageId);
      refetchPageAssessmentStatus(store, pageId);
    }, DEBOUNCE_MS),
  );
}

/**
 * When submissions, assessments, or page publish state change via pubsub, `pageAssessmentStatuses`
 * does not update by itself. Debounce-refetch per page from the bulk status endpoint when cached
 * status may be stale.
 *
 * Registers from the section course layout (e.g. `[sectionId].tsx`) while mounted; does not register
 * globally, since home/org views do not receive section-scoped model updates.
 *
 * Students do not receive assessment pubsub until publish (`get_target_user_ids` is empty while
 * unpublished). Publish itself is a page update; the pages slice applies that update before this
 * listener runs, so we cannot diff against store page state. Instead we compare the incoming
 * publish flag to cached assessment status (same idea as the submission/assessment branches).
 * Official assessments are also rebroadcast on `release_assessments` so the assessment panel can render.
 */
export function registerPageAssessmentStatusPubsubListener(store: Store<RootState>): () => void {
  return addBroadcastListener(BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE, (message) => {
    if (message.type !== "model-update" || !["create", "update"].includes(message.action)) {
      return;
    }

    if (message.model === "pages") {
      const page = message.data as PageType;
      if (!page?.id) {
        return;
      }

      const status = selectAssessmentInfoForPage(store.getState(), page.id)?.status;
      const isPublished = !!page.assessments_published_at;
      // Publish should move student status toward ASSESSED; unpublish away from it.
      if (isPublished && status !== AssessmentStatus.ASSESSED) {
        scheduleRefresh(store, page.id);
      } else if (!isPublished && status === AssessmentStatus.ASSESSED) {
        scheduleRefresh(store, page.id);
      }
      return;
    }

    if (message.model === "submissions") {
      const submission = message.data as SubmissionType;
      const pageId = submission.page_id;
      if (!pageId) {
        return;
      }

      const state = store.getState();
      const status = selectAssessmentInfoForPage(state, pageId)?.status;
      if (status === AssessmentStatus.NOT_ASSESSABLE || status === undefined) {
        scheduleRefresh(store, pageId);
      }
      return;
    }

    if (message.model === "assessments") {
      const assessment = message.data as AssessmentType;
      const submissionId = assessment.submission_id;
      if (!submissionId) {
        return;
      }

      const state = store.getState();
      const submission = submissionSelectors.selectById(state, submissionId);
      const pageId = submission?.page_id;
      if (!pageId) {
        return;
      }

      const status = selectAssessmentInfoForPage(state, pageId)?.status;
      if (status !== AssessmentStatus.ASSESSED) {
        scheduleRefresh(store, pageId);
      }
    }
  });
}
